import { TurnCommands } from '../types';

/**
 * Lv6 達人 AI の混合戦略選択
 *
 * α-β 探索で得られた候補手ペアのスコアから、softmax 風の確率分布で
 * 1 つを選ぶ。完全決定論的な選択（常に最高スコア）だと相手に読まれる
 * 可能性があるため、上位手の中から確率的に散らす。
 *
 * - mixedStrategy=0: 完全決定論（最高スコアのみ）
 * - mixedStrategy=100: 強い混合（候補がフラットに近い）
 *
 * 純粋関数。同じ randomFn と入力で同じ結果を返す。
 */

export interface ScoredCommand {
  command: TurnCommands;
  score: number;
}

/**
 * Softmax 風の温度付き確率分布で 1 手を選ぶ
 *
 * - mixedStrategy=0: temperature=∞（実質 argmax）
 *   → 最高スコアの手だけ確率1
 * - mixedStrategy=50: temperature=1（標準的な softmax）
 * - mixedStrategy=100: temperature=0.01（極めてフラット）
 *
 * 内部的には:
 * - 全候補のスコアを「最高スコアからの差」に正規化
 * - exp(-差 / temperature) で重み計算
 * - スコア比例ランダム選択
 *
 * @param candidates スコア付き候補リスト（空でないこと）
 * @param mixedStrategy 0-100。0=完全決定論、100=最大分散
 * @param randomFn 乱数関数 (0-1)
 * @returns 選ばれたコマンド
 */
export function selectViaMixedStrategy(
  candidates: ScoredCommand[],
  mixedStrategy: number,
  randomFn: () => number,
): TurnCommands {
  if (candidates.length === 0) {
    throw new Error('selectViaMixedStrategy: candidates must not be empty');
  }
  if (candidates.length === 1) {
    return candidates[0].command;
  }

  // mixedStrategy=0 → argmax
  if (mixedStrategy <= 0) {
    return getArgmaxCommand(candidates);
  }

  // 上位 N 個に絞る（N=5）
  const topN = candidates
    .slice()
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  // mixedStrategy を temperature にマッピング
  // 0 → 0.01（実質 argmax）, 50 → 1.0, 100 → 100（フラット）
  // 指数的にスケール: temperature = 0.01 * 1000^(strength)
  const strength = mixedStrategy / 100;
  const temperature = 0.01 * Math.pow(1000, strength);

  // スコアを最高値からの差として正規化
  const maxScore = topN[0].score;
  const weights = topN.map((c) => Math.exp(-(maxScore - c.score) / temperature));
  const totalWeight = weights.reduce((s, w) => s + w, 0);

  if (totalWeight <= 0 || !isFinite(totalWeight)) {
    return topN[0].command;
  }

  // スコア比例ランダム選択
  let r = randomFn() * totalWeight;
  for (let i = 0; i < topN.length; i++) {
    r -= weights[i];
    if (r <= 0) return topN[i].command;
  }
  return topN[topN.length - 1].command;
}

/**
 * 最高スコアの手を取得（タイブレーク: 先頭）
 */
function getArgmaxCommand(candidates: ScoredCommand[]): TurnCommands {
  let best = candidates[0];
  for (let i = 1; i < candidates.length; i++) {
    if (candidates[i].score > best.score) {
      best = candidates[i];
    }
  }
  return best.command;
}
